import { useMemo } from 'react';

import { useSharedPluginStateSelector } from '@atlaskit/editor-common/useSharedPluginStateSelector';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import type { EditorOfflineStoragePlugin } from '@confluence/editor-offline-storage/entry-points/editorOfflineStoragePluginType.ts';

type Options = {
	disabled: boolean;
	collabEditInternetLost: boolean;
	internetDisconnected: boolean;
	api: PublicPluginAPI<[EditorOfflineStoragePlugin]> | undefined;
};

/**
 * Hook to determine if we should disable the editor contents based on the current
 * state of connectivity.
 *
 * @param options Options for hook
 * @param options.disabled If the editor component is currently disabled
 * @param options.collabEditInternetLost If the collab edit has disconnected
 * @param options.internetDisconnected If the internet is disconnected
 * @param options.api Editor API to listen to the state of plugins
 * @returns boolean - if the component is disabled
 */
export const useEditorContentDisabled = ({
	disabled,
	collabEditInternetLost,
	internetDisconnected,
	api,
}: Options): boolean => {
	const isOfflineStorageDisabled =
		useSharedPluginStateSelector(api, 'editorOfflineStorage.disabled') ?? false;

	const inactiveSyncing =
		useSharedPluginStateSelector(api, 'editorOfflineStorage.inactiveSyncing') ?? false;

	return useMemo(() => {
		if (inactiveSyncing) {
			return true;
		}
		if (isOfflineStorageDisabled) {
			return disabled;
		}
		return disabled && !(collabEditInternetLost || internetDisconnected);
	}, [
		collabEditInternetLost,
		internetDisconnected,
		disabled,
		isOfflineStorageDisabled,
		inactiveSyncing,
	]);
};
